import { useEffect, useState } from "react";

import type { SessionSummary } from "./auth/authApiLocalClient";
import { fetchCurrentSessionUid, fetchMySessions, revokeSession } from "./auth/authApiLocalClient";

interface SessionsViewProps {
  /** Called after ending *this device's own* session -- same as pressing "Выйти" in the account menu. */
  onLogOut: () => void;
}

// null covers both "this session predates client_platform existing" and
// "this client hasn't been updated to send it yet" -- deliberately not
// distinguished, both just mean "we don't know", same as kwms-web's own.
function platformLabel(platform: string | null): string {
  if (platform === "web") return "Веб";
  if (platform === "android") return "Android";
  return "Платформа неизвестна";
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
}

/**
 * The one thing this cabinet's own "Личный кабинет" can actually show
 * today -- there's no editable name/email/passkey-toggle here the way
 * kwms-web's own CabinetPage has, since those live in registration-api and
 * need a global access_token this app never holds onto (see
 * authApiLocalClient.ts's own doc comment on why). `GET /sessions/mine`
 * already returns active sessions only (nodeowner-pg's own
 * authusersession_getbyauthuser filters revoked_at/expires_at), unlike
 * kwms-web's equivalent which lists everything and filters client-side.
 */
export function SessionsView({ onLogOut }: SessionsViewProps) {
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [currentSessionUid, setCurrentSessionUid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokingUid, setRevokingUid] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchMySessions(), fetchCurrentSessionUid()])
      .then(([sessionList, current]) => {
        setSessions(sessionList ?? []);
        setCurrentSessionUid(current);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Не удалось получить список сессий"));
  }, []);

  async function endSession(sessionUid: string) {
    if (revokingUid) return;
    setRevokingUid(sessionUid);
    setError(null);
    try {
      await revokeSession(sessionUid);
      // Ending the session this very request is authenticated with logs
      // this device out for real, not just out of the list.
      if (sessionUid === currentSessionUid) {
        onLogOut();
        return;
      }
      setSessions((prev) => (prev ? prev.filter((s) => s.session_uid !== sessionUid) : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось завершить сессию");
    } finally {
      setRevokingUid(null);
    }
  }

  return (
    <div className="page-content">
      <div className="card" style={{ maxWidth: "none" }}>
        {error && <p className="error-text" role="alert">{error}</p>}

        {sessions === null ? (
          <p className="status-text">Загружаем…</p>
        ) : sessions.length === 0 ? (
          <p className="status-text">Нет активных сессий</p>
        ) : (
          sessions.map((session, index) => {
            const isCurrent = session.session_uid === currentSessionUid;
            return (
              <div key={session.session_uid}>
                {index > 0 && <hr className="divider" />}
                <div className="field">
                  <span className="field-label">{isCurrent ? "Это устройство" : "Устройство"}</span>
                  <span className="field-value">
                    {platformLabel(session.client_platform)} · Вход {formatDateTime(session.created_at)}
                  </span>
                  {session.client_version && <span className="pill">{session.client_version}</span>}
                </div>
                <button className="btn btn-danger" onClick={() => endSession(session.session_uid)} disabled={revokingUid !== null}>
                  {revokingUid === session.session_uid ? "Завершаем…" : "Завершить"}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
